/**
 * Default fetch and URL builder for bike station bbox preload.
 */

import axios from "axios";

import type { BikeStationDTO } from "../../../types/dtos/api/bike-station.dto";
import type { MapBbox } from "../bboxPreload/bboxPreload.types";

/**
 * Builds the API query URL for GET /api/pois/bike-stations from a preload bbox.
 *
 * @param bbox Preload bounds.
 * @returns URL string with query parameters.
 */
export const buildBikeStationsBboxApiUrl = ( bbox: MapBbox ): string => `/api/pois/bike-stations?minLat=${ bbox.minLat }&maxLat=${ bbox.maxLat }&minLon=${ bbox.minLon }&maxLon=${ bbox.maxLon }`;

/**
 * Fetches bike stations for a geographic bbox (injectable for tests).
 *
 * @param bbox Preload bounds.
 * @param signal Optional abort signal when the store is reset during flight.
 * @returns Array of bike station DTOs.
 */
export type FetchBikeStationsForBbox = (
    bbox: MapBbox,
    signal?: AbortSignal,
) => Promise<BikeStationDTO[]>;

/**
 * Default production fetch using the bike stations HTTP API.
 *
 * @param bbox Preload bounds.
 * @param signal Optional abort signal.
 * @returns Parsed bike station list.
 */
export const defaultFetchBikeStationsForBbox: FetchBikeStationsForBbox = async(
    bbox: MapBbox,
    signal?: AbortSignal,
): Promise<BikeStationDTO[]> => {
    const response = await axios.get<BikeStationDTO[]>(
        buildBikeStationsBboxApiUrl( bbox ),
        { "signal": signal },
    );

    return response.data;
};

/**
 * Logs bike station fetch errors.
 *
 * @param error Thrown error.
 * @param signal Abort signal for the failed request.
 */
export const handleBikeStationsFetchError = ( error: unknown, signal: AbortSignal ): void => {
    if ( signal.aborted ) {
        return;
    }

    if ( axios.isAxiosError( error ) ) {
        console.error( "Bike stations fetch failed", error.response?.status );
    } else {
        console.error( "Bike stations fetch failed", error );
    }
};
